import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  Box,
  Image,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import NextLink from 'next/link';

interface HomePopupBannerProps {
  isEnabled?: boolean;
  imageUrl?: string;
  altText?: string;
  width?: number;
  height?: number;
  link?: string;
  showDelay?: number; // delay in milliseconds before showing popup
  useLocalStorage?: boolean; // use localStorage instead of sessionStorage
  localStorageTimeout?: number; // timeout in hours before showing again (only works with localStorage)
  storageKey?: string; // custom storage key
}

const HomePopupBanner: React.FC<HomePopupBannerProps> = ({
  isEnabled = true,
  imageUrl = '/image-placeholder-plus.png',
  altText = 'Popup Banner',
  width = 600,
  height = 400,
  link = '',
  showDelay = 2000,
  useLocalStorage = false,
  localStorageTimeout = 24, // 24 hours default
  storageKey = 'homePopupBannerDismissed',
}) => {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!isEnabled) return;

    const storage = useLocalStorage ? localStorage : sessionStorage;
    const dismissedData = storage.getItem(storageKey);

    if (dismissedData) {
      if (useLocalStorage) {
        try {
          const { timestamp } = JSON.parse(dismissedData);
          const now = new Date().getTime();
          const timeoutMs = localStorageTimeout * 60 * 60 * 1000; // convert hours to milliseconds

          // Check if timeout has passed
          if (now - timestamp < timeoutMs) {
            return; // Still within timeout period, don't show popup
          } else {
            // Timeout has passed, remove the storage item
            storage.removeItem(storageKey);
          }
        } catch (error) {
          // Invalid JSON, remove the item
          storage.removeItem(storageKey);
        }
      } else {
        // For sessionStorage, just check if item exists
        return;
      }
    }

    // Show popup after delay
    const timer = setTimeout(() => {
      setIsOpen(true);
    }, showDelay);

    return () => clearTimeout(timer);
  }, [isEnabled, showDelay, useLocalStorage, localStorageTimeout, storageKey]);

  const handleClose = () => {
    setIsOpen(false);

    const storage = useLocalStorage ? localStorage : sessionStorage;

    if (useLocalStorage) {
      // Store timestamp with localStorage for timeout functionality
      const dismissedData = {
        timestamp: new Date().getTime(),
        dismissed: true,
      };
      storage.setItem(storageKey, JSON.stringify(dismissedData));
    } else {
      // For sessionStorage, just store a simple flag
      storage.setItem(storageKey, 'true');
    }
  };

  const handleImageClick = () => {
    if (link) {
      handleClose();
    }
  };

  if (!isEnabled) return null;

  const imageElement = (
    <Image
      src={imageUrl}
      alt={altText}
      width="100%"
      height="auto"
      objectFit="contain"
      cursor={link ? 'pointer' : 'default'}
      onClick={handleImageClick}
      borderRadius="lg"
      _hover={
        link
          ? {
              opacity: 0.95,
              transform: 'scale(1.02)',
            }
          : {}
      }
      transition="all 0.3s ease"
    />
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="xl"
      closeOnOverlayClick={true}
    >
      <ModalOverlay bg="blackAlpha.700" />
      <ModalContent
        maxWidth={{
          base: '90vw',
          md: '80vw',
          lg: '60vw',
          xl: '50vw',
        }}
        bg="transparent"
        boxShadow="none"
        position="relative"
        mx="auto"
        p={4}
      >
        <ModalCloseButton
          position="absolute"
          top="8px"
          right="8px"
          bg="white"
          color="black"
          borderRadius="full"
          size="md"
          zIndex={10}
          boxShadow="lg"
          _hover={{
            bg: 'gray.100',
            transform: 'scale(1.1)',
          }}
          _active={{
            transform: 'scale(0.95)',
          }}
          transition="all 0.2s"
        />
        <Box
          borderRadius="xl"
          overflow="hidden"
          boxShadow="2xl"
          bg="white"
          p={2}
        >
          {link ? (
            <NextLink href={link} target="_blank">
              {imageElement}
            </NextLink>
          ) : (
            imageElement
          )}
        </Box>
      </ModalContent>
    </Modal>
  );
};

export default HomePopupBanner;
